<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth } from '../../composables/useAuth'
import { getCmNotificationCount } from '../../services/cmApi'

const { getAccessTokenSilently } = useAuth()
const router = useRouter()

const isOpen = ref(false)
const loading = ref(false)
const error = ref<string | null>(null)

interface CmNotificationCounts {
    total: number
    overdueCadences: number
    criticalFriction: number
    stalledDeals: number
    churnRisks: number
    playbookStepsDue: number
}

const counts = ref<CmNotificationCounts>({
    total: 0,
    overdueCadences: 0,
    criticalFriction: 0,
    stalledDeals: 0,
    churnRisks: 0,
    playbookStepsDue: 0
})

const POLL_INTERVAL = 60000 // 60 seconds
let pollTimer: ReturnType<typeof setInterval> | null = null

const hasNotifications = computed(() => counts.value.total > 0)
const displayCount = computed(() => counts.value.total > 99 ? '99+' : counts.value.total.toString())

interface NotificationRow {
    label: string
    count: number
    icon: string
    color: string
    route: string
}

const rows = computed<NotificationRow[]>(() => {
    const items: NotificationRow[] = []

    if (counts.value.overdueCadences > 0) {
        items.push({
            label: 'Cadence Overdue',
            count: counts.value.overdueCadences,
            icon: 'clock',
            color: 'text-orange-500',
            route: '/cm/relationships'
        })
    }

    if (counts.value.churnRisks > 0) {
        items.push({
            label: 'Churn Risks',
            count: counts.value.churnRisks,
            icon: 'trending-down',
            color: 'text-red-500',
            route: '/cm/intelligence?tab=signals'
        })
    }

    if (counts.value.stalledDeals > 0) {
        items.push({
            label: 'Stalled Deals',
            count: counts.value.stalledDeals,
            icon: 'pause-circle',
            color: 'text-amber-500',
            route: '/cm/pipeline?stage=STALLED'
        })
    }

    if (counts.value.playbookStepsDue > 0) {
        items.push({
            label: 'Playbook Steps Due',
            count: counts.value.playbookStepsDue,
            icon: 'book-open',
            color: 'text-purple-500',
            route: '/cm/tasks?filter=overdue'
        })
    }

    if (counts.value.criticalFriction > 0) {
        items.push({
            label: 'Critical Friction',
            count: counts.value.criticalFriction,
            icon: 'alert-triangle',
            color: 'text-red-600',
            route: '/cm/intelligence?tab=diagnostics'
        })
    }

    return items
})

async function fetchCounts() {
    try {
        const token = await getAccessTokenSilently()
        const data = await getCmNotificationCount(token)
        counts.value = {
            total: data.total ?? 0,
            overdueCadences: data.overdueCadences ?? 0,
            criticalFriction: data.criticalFriction ?? 0,
            stalledDeals: data.stalledDeals ?? 0,
            churnRisks: data.churnRisks ?? 0,
            playbookStepsDue: data.playbookStepsDue ?? 0
        }
    } catch (e) {
        console.error('Failed to fetch CM notification counts:', e)
    }
}

async function fetchWithLoading() {
    loading.value = true
    error.value = null
    try {
        await fetchCounts()
    } catch {
        error.value = 'Failed to load notifications'
    } finally {
        loading.value = false
    }
}

function toggleDropdown() {
    isOpen.value = !isOpen.value
    if (isOpen.value) {
        fetchWithLoading()
    }
}

function closeDropdown() {
    isOpen.value = false
}

function navigateTo(route: string) {
    router.push(route)
    closeDropdown()
}

function handleClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement
    if (!target.closest('.cm-notification-bell-container')) {
        closeDropdown()
    }
}

onMounted(() => {
    fetchCounts()
    pollTimer = setInterval(fetchCounts, POLL_INTERVAL)
    document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
    if (pollTimer) clearInterval(pollTimer)
    document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
    <div class="cm-notification-bell-container relative">
        <!-- Bell Button -->
        <button
            @click="toggleDropdown"
            class="relative p-2 text-cc-text-secondary hover:text-cc-text focus:outline-none focus-visible:ring-2 focus-visible:ring-cc-primary rounded-lg transition-colors"
            :aria-label="`Notifications${hasNotifications ? ` (${counts.total} items)` : ''}`"
        >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>

            <!-- Count Badge -->
            <span
                v-if="hasNotifications"
                class="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center px-1 py-0.5 text-[10px] font-bold leading-none text-white bg-red-500 rounded-full min-w-[16px] h-4"
            >
                {{ displayCount }}
            </span>
        </button>

        <!-- Dropdown Panel -->
        <Transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
        >
            <div
                v-if="isOpen"
                class="absolute right-0 top-12 w-80 bg-cc-elevated rounded-cc overflow-hidden z-50"
                style="box-shadow: var(--shadow-dropdown)"
            >
                <!-- Header -->
                <div class="px-4 py-3 border-b border-cc-border/20">
                    <h3 class="font-semibold text-cc-text text-sm">Notifications</h3>
                </div>

                <!-- Loading -->
                <div v-if="loading" class="p-6 text-center">
                    <div class="animate-spin w-5 h-5 border-2 border-cc-primary border-t-transparent rounded-full mx-auto"></div>
                </div>

                <!-- Error -->
                <div v-else-if="error" class="p-4 text-center text-red-500 text-sm">
                    {{ error }}
                </div>

                <!-- Empty State -->
                <div v-else-if="rows.length === 0" class="p-6 text-center text-cc-text-tertiary text-sm">
                    All clear -- no action items
                </div>

                <!-- Notification Rows -->
                <div v-else>
                    <button
                        v-for="row in rows"
                        :key="row.label"
                        @click="navigateTo(row.route)"
                        class="w-full flex items-center gap-3 px-4 py-3 hover:bg-cc-hover transition-colors text-left border-b border-cc-border/10 last:border-b-0"
                    >
                        <!-- Icon -->
                        <div class="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-cc-surface">
                            <!-- Clock icon for Cadence -->
                            <svg v-if="row.icon === 'clock'" class="w-4 h-4" :class="row.color" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <!-- Trending Down for Churn -->
                            <svg v-else-if="row.icon === 'trending-down'" class="w-4 h-4" :class="row.color" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                            </svg>
                            <!-- Pause Circle for Stalled -->
                            <svg v-else-if="row.icon === 'pause-circle'" class="w-4 h-4" :class="row.color" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <!-- Book for Playbook -->
                            <svg v-else-if="row.icon === 'book-open'" class="w-4 h-4" :class="row.color" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                            <!-- Alert Triangle for Friction -->
                            <svg v-else-if="row.icon === 'alert-triangle'" class="w-4 h-4" :class="row.color" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>

                        <!-- Label + Count -->
                        <div class="flex-1 min-w-0">
                            <span class="text-sm text-cc-text">{{ row.label }}</span>
                        </div>

                        <!-- Count Badge -->
                        <span class="flex-shrink-0 inline-flex items-center justify-center min-w-[24px] h-6 px-2 text-xs font-semibold rounded-full bg-cc-surface text-cc-text">
                            {{ row.count }}
                        </span>

                        <!-- Chevron -->
                        <svg class="w-4 h-4 text-cc-text-tertiary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>

                <!-- Footer -->
                <div class="px-4 py-2.5 border-t border-cc-border/20 text-center">
                    <button
                        @click="navigateTo('/cm/today')"
                        class="text-xs text-cc-primary hover:text-cc-primary-hover font-medium"
                    >
                        View Today's Actions
                    </button>
                </div>
            </div>
        </Transition>
    </div>
</template>
